import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { useTranslation } from "react-i18next";

/**
 * old SearchOptsGroup 포팅 — 화면별로 고정된 조회조건 필드 목록을 그대로 나열 렌더링하는
 * 순수 렌더링 컴포넌트. DB(search_option, attr08Val)로 표시 유무를 동적 제어하던 기능은
 * 제거됐고, 표시할 필드는 화면(부모)이 fieldKeys로 고정 지정한다.
 * 값 소유권은 100% 화면(부모)에 있음 — old의 ref+getValues() pull 방식 불필요.
 * component-mapping-table.md §10 / fp-master-screen-guide.md §10 참고.
 *
 * props:
 *  - fields: { [tpCd]: value } — 화면이 미리 선언한 필드 전체(§6-1 대상)
 *  - fieldKeys: string[] — 이번 렌더링에서 보여줄 필드 키 목록(고정, 화면/탭이 직접 지정)
 *  - onChange: (tpCd, value) => void
 *  - labels: { [tpCd]: i18nKey } — 필드별 라벨 i18n 키(화면이 이미 알고 있는 고정 라벨)
 *  - fieldTypes?: { [tpCd]: "text" | "select" } — 필드별 입력 컨트롤 타입(기본 text)
 *  - fieldProps?: { [tpCd]: object } — TextField 등에 그대로 전달할 추가 props(예: minWidth)
 *  - selectOptions?: { [tpCd]: Array<{ value, label }> } — type==="select"일 때 옵션 목록
 */
const renderSearchField = ({ tpCd, value, onChange, label, type, others, selectOptions }) => {
  switch (type) {
    case "select":
      return (
        // text 케이스(TextField)와 동일하게 ml로 PlanScope/이전 필드와의 간격을 주고, label은
        // FormControl+InputLabel로(raw Select는 label을 못 붙임) 붙인다.
        <FormControl key={tpCd} size="small" sx={{ ml: 1.5, minWidth: 220 }} {...others}>
          <InputLabel id={`search-opt-${tpCd}-label`}>{label}</InputLabel>
          <Select
            labelId={`search-opt-${tpCd}-label`}
            label={label}
            value={value ?? ""}
            onChange={e => onChange(e.target.value)}
          >
            {(selectOptions ?? []).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      );
    case "text":
    default:
      return (
        <TextField
          key={tpCd}
          label={label}
          value={value ?? ""}
          onChange={e => onChange(e.target.value)}
          size="small"
          // PlanScope(Autocomplete, 마진 없음)와 자연 높이를 맞춰야 FilterContainer의 래퍼 Box에서
          // 두 컨트롤이 같은 y위치에서 시작해 수직 중앙이 맞는다 — 세로 마진(my)을 주면 그만큼 아래로
          // 밀려 PlanScope와 어긋난다(줄바꿈 시 행간 여백은 FilterContainer의 래퍼 Box가 pb로 이미 처리).
          sx={{ ml: 1.5, minWidth: 150 }}
          {...others}
        />
      );
  }
};

const SearchOptsGroup = ({ fields, fieldKeys, onChange, labels = {}, fieldTypes = {}, fieldProps = {}, selectOptions = {} }) => {
  const { t } = useTranslation();

  return fieldKeys.map(tpCd => renderSearchField({
    tpCd,
    value: fields[tpCd],
    onChange: (value) => onChange(tpCd, value),
    label: t(labels[tpCd] ?? tpCd),
    type: fieldTypes[tpCd],
    others: fieldProps[tpCd],
    selectOptions: selectOptions[tpCd],
  }));
};

export default SearchOptsGroup;
